import MESSAGES from 'constants/messages';
import { stringMaxLengthIsInvalid } from 'store/utils/validators';

import ih from 'immutability-helper';

import _set from 'lodash/set';
import _get from 'lodash/get';
import _size from 'lodash/size';
import _map from 'lodash/map';
import _find from 'lodash/find';
import _inRange from 'lodash/inRange';
import _isEmpty from 'lodash/isEmpty';

import { CONDITION_TYPE, ACTION_TYPE, FEATURE_VALUE_TYPE } from '../constants';

/* eslint-disable @typescript-eslint/camelcase */

// VALIDATORS:
const isNameFieldInvalid = str => !str.match(/^[a-zA-Z]+[a-zA-Z0-9_.<>[\]-]*$/);

// HELPERS:
const getId = i => (i && i.id) || '';

export const getMarkSpanFromAndToFieldsErrors = (action, rootPath) => {
  let fromAndToFieldsErrors = {};
  const from = _get(action, 'mark_action.matching_pattern_element_from', false);
  const to = _get(action, 'mark_action.matching_pattern_element_to', false);

  // Only one field either from or to has value
  const userFilledOnlyOneFromTo = (from && !to) || (!from && to);

  if (userFilledOnlyOneFromTo) {
    if (from) {
      fromAndToFieldsErrors = {
        ...fromAndToFieldsErrors,
        [`${rootPath}.matching_pattern_element_to`]: MESSAGES.RULES.ACTIONS.MARK
          .MATCHING_PATTERN_ELEMENT_TO.REQUIRED,
      };
    } else {
      fromAndToFieldsErrors = {
        ...fromAndToFieldsErrors,
        [`${rootPath}.matching_pattern_element_from`]: MESSAGES.RULES.ACTIONS
          .MARK.MATCHING_PATTERN_ELEMENT_FROM.REQUIRED,
      };
    }
  }

  if (from && to && from.order > to.order) {
    fromAndToFieldsErrors = {
      ...fromAndToFieldsErrors,
      [`${rootPath}.matching_pattern_element_to`]: MESSAGES.RULES.ACTIONS.SHIFT
        .MATCHING_PATTERN_TO.VALUE,
    };
  }

  return fromAndToFieldsErrors;
};

export const validate = (fields, { values, orderList }) => {
  const errors = {};

  if (!fields.name || isNameFieldInvalid(fields.name)) {
    errors.name = MESSAGES.RULES.NAME.REGEX.UPDATE;
  }

  if (stringMaxLengthIsInvalid(fields.name)) {
    errors.name = MESSAGES.RULES.NAME.MAX_LENGTH;
  }

  // CONDITIONS:
  fields.conditions.forEach((cond, i) => {
    const rootPath = `conditions[${i}]`;

    if (cond.condition_type === CONDITION_TYPE.ENTITY) {
      if (!_get(cond, 'entity_condition.entity')) {
        _set(
          errors,
          `${rootPath}.entity_condition.entity`,
          MESSAGES.RULES.CONDITIONS.ENTITY_COND.ENTITY.REQUIRED,
        );
      }

      if (!_get(cond, 'entity_condition.matching_pattern_element')) {
        _set(
          errors,
          `${rootPath}.entity_condition.matching_pattern_element`,
          MESSAGES.RULES.CONDITIONS.ENTITY_COND.MATCHING_PATTERN.REQUIRED,
        );
      }

      if (!_get(cond, 'entity_condition.operator')) {
        _set(
          errors,
          `${rootPath}.entity_condition.operator`,
          MESSAGES.RULES.CONDITIONS.ENTITY_COND.OPERATOR.REQUIRED,
        );
      }
    }

    if (cond.condition_type === CONDITION_TYPE.REGEXP) {
      if (!_get(cond, 'regexp_condition.matching_pattern_element')) {
        _set(
          errors,
          `${rootPath}.regexp_condition.matching_pattern_element`,
          MESSAGES.RULES.CONDITIONS.REGEX_COND.MATCHING_PATTERN.REQUIRED,
        );
      }

      if (!_get(cond, 'regexp_condition.regexp')) {
        _set(
          errors,
          `${rootPath}.regexp_condition.regexp`,
          MESSAGES.RULES.CONDITIONS.REGEX_COND.REGEX.REQUIRED,
        );
      }
    }

    if (cond.condition_type === CONDITION_TYPE.FEATURE) {
      if (!_get(cond, 'entity_feature_condition.matching_pattern_element')) {
        _set(
          errors,
          `${rootPath}.entity_feature_condition.matching_pattern_element`,
          MESSAGES.RULES.CONDITIONS.FEATURE_COND.MATCHING_PATTERN.REQUIRED,
        );
      }

      if (!_get(cond, 'entity_feature_condition.entity_feature')) {
        _set(
          errors,
          `${rootPath}.entity_feature_condition.entity_feature`,
          MESSAGES.RULES.CONDITIONS.FEATURE_COND.FEATURE.REQUIRED,
        );
      }

      if (
        !_get(cond, 'entity_feature_condition.values') ||
        !_size(cond.entity_feature_condition.values)
      ) {
        _set(
          errors,
          `${rootPath}.entity_feature_condition.values`,
          MESSAGES.RULES.CONDITIONS.FEATURE_COND.VALUES.REQUIRED,
        );
      }
    }
  });

  // ACTIONS
  fields.actions.forEach((action, i) => {
    if (action) {
      if (action.action_type === ACTION_TYPE.MARK) {
        const rootPath = `actions[${i}].mark_action`;

        if (!_get(action, 'mark_action.entity')) {
          _set(
            errors,
            `${rootPath}.entity`,
            MESSAGES.RULES.ACTIONS.MARK.ENTITY.REQUIRED,
          );
        }

        const markSpanFromAndToFieldsErrors = getMarkSpanFromAndToFieldsErrors(
          action,
          rootPath,
        );

        Object.entries(markSpanFromAndToFieldsErrors).forEach(
          ([errorPath, errorMessage]) => {
            _set(errors, errorPath, errorMessage);
          },
        );

        if (_get(action, 'mark_action.feature_values')) {
          action.mark_action.feature_values.forEach((feature, j) => {
            const path = `${rootPath}.feature_values[${j}]`;

            if (feature.value_type === FEATURE_VALUE_TYPE.EXPLICIT) {
              if (_isEmpty(feature.explicit_value)) {
                _set(
                  errors,
                  `${path}.explicit_value`,
                  MESSAGES.RULES.ACTIONS.MARK.EXPLICIT.REQUIRED,
                );
              }
            }

            if (feature.value_type === FEATURE_VALUE_TYPE.DYNAMIC) {
              if (
                !_get(feature, 'dynamic_value.source_matching_pattern_element')
              ) {
                _set(
                  errors,
                  `${path}.dynamic_value.source_matching_pattern_element`,
                  MESSAGES.RULES.ACTIONS.MARK.DYNAMIC.MATCHING_PATTERN.REQUIRED,
                );
              }

              if (!_get(feature, 'dynamic_value.source_entity_feature')) {
                _set(
                  errors,
                  `${path}.dynamic_value.source_entity_feature`,
                  MESSAGES.RULES.ACTIONS.MARK.DYNAMIC.FEATURE.REQUIRED,
                );
              }
            }

            if (
              feature.value_type === FEATURE_VALUE_TYPE.CONCATENATION &&
              _size(feature.concatenation_value)
            ) {
              feature.concatenation_value.forEach((cvalue, c) => {
                const path = `${rootPath}.feature_values[${j}].concatenation_value[${c}]`;

                if (!_get(cvalue, 'source_matching_pattern_element')) {
                  _set(
                    errors,
                    `${path}.source_matching_pattern_element`,
                    MESSAGES.RULES.ACTIONS.MARK.CONCATENATION.MATCHING_PATTERN
                      .REQUIRED,
                  );
                }

                if (!_get(cvalue, 'source_entity_feature')) {
                  _set(
                    errors,
                    `${path}.source_entity_feature`,
                    MESSAGES.RULES.ACTIONS.MARK.CONCATENATION.FEATURE.REQUIRED,
                  );
                }
              });
            }
          });
        }
      }

      if (action.action_type === ACTION_TYPE.UNMARK) {
        if (!_get(action, 'unmark_action.matching_pattern_element')) {
          _set(
            errors,
            `actions[${i}].unmark_action.matching_pattern_element`,
            MESSAGES.RULES.ACTIONS.UNMARK.MATCHING_PATTERN.REQUIRED,
          );
        }
      }

      if (action.action_type === ACTION_TYPE.SHIFT) {
        const rootPath = `actions[${i}].shift_action`;

        const to = _get(action, 'shift_action.matching_pattern_element_to');
        const from = _get(action, 'shift_action.matching_pattern_element_from');

        if (!_get(action, 'shift_action.matching_pattern_element')) {
          _set(
            errors,
            `${rootPath}.matching_pattern_element`,
            MESSAGES.RULES.ACTIONS.SHIFT.MATCHING_PATTERN.REQUIRED,
          );
        }

        if (!from) {
          _set(
            errors,
            `${rootPath}.matching_pattern_element_from`,
            MESSAGES.RULES.ACTIONS.SHIFT.MATCHING_PATTERN_FROM.REQUIRED,
          );
        }

        if (!to) {
          _set(
            errors,
            `${rootPath}.matching_pattern_element_to`,
            MESSAGES.RULES.ACTIONS.SHIFT.MATCHING_PATTERN_TO.REQUIRED,
          );
        }

        if (from && to && from.order > to.order) {
          _set(
            errors,
            `${rootPath}.matching_pattern_element_to`,
            MESSAGES.RULES.ACTIONS.SHIFT.MATCHING_PATTERN_TO.VALUE,
          );
        }
      }

      if (action.action_type === ACTION_TYPE.FILL) {
        const rootPath = `actions[${i}].fill_action`;

        if (!_get(action, 'fill_action.matching_pattern_element')) {
          _set(
            errors,
            `${rootPath}.matching_pattern_element`,
            MESSAGES.RULES.ACTIONS.MARK.ENTITY.REQUIRED,
          );
        }

        if (_get(action, 'fill_action.feature_values')) {
          // fill action should have completed at lease one value_type.
          const featureWithEmptyType = action.fill_action.feature_values.filter(
            feature => !feature.value_type,
          );

          action.fill_action.feature_values.forEach((feature, j) => {
            const path = `${rootPath}.feature_values[${j}]`;

            if (feature.value_type === FEATURE_VALUE_TYPE.EXPLICIT) {
              if (_isEmpty(feature.explicit_value)) {
                _set(
                  errors,
                  `${path}.explicit_value`,
                  MESSAGES.RULES.ACTIONS.FILL.EXPLICIT.REQUIRED,
                );
              }
            }

            if (feature.value_type === FEATURE_VALUE_TYPE.DYNAMIC) {
              if (
                !_get(feature, 'dynamic_value.source_matching_pattern_element')
              ) {
                _set(
                  errors,
                  `${path}.dynamic_value.source_matching_pattern_element`,
                  MESSAGES.RULES.ACTIONS.FILL.DYNAMIC.MATCHING_PATTERN.REQUIRED,
                );
              }

              if (!_get(feature, 'dynamic_value.source_entity_feature')) {
                _set(
                  errors,
                  `${path}.dynamic_value.source_entity_feature`,
                  MESSAGES.RULES.ACTIONS.FILL.DYNAMIC.FEATURE.REQUIRED,
                );
              }
            }

            if (
              feature.value_type === FEATURE_VALUE_TYPE.CONCATENATION &&
              _size(feature.concatenation_value)
            ) {
              feature.concatenation_value.forEach((cvalue, c) => {
                const path = `${rootPath}.feature_values[${j}].concatenation_value[${c}]`;

                if (!_get(cvalue, 'source_matching_pattern_element')) {
                  _set(
                    errors,
                    `${path}.source_matching_pattern_element`,
                    MESSAGES.RULES.ACTIONS.FILL.CONCATENATION.MATCHING_PATTERN
                      .REQUIRED,
                  );
                }

                if (!_get(cvalue, 'source_entity_feature')) {
                  _set(
                    errors,
                    `${path}.source_entity_feature`,
                    MESSAGES.RULES.ACTIONS.FILL.CONCATENATION.FEATURE.REQUIRED,
                  );
                }
              });
            }

            // At lease one value_type should be completed
            if (
              !feature.value_type &&
              _size(action.fill_action.feature_values) ===
                _size(featureWithEmptyType)
            ) {
              _set(
                errors,
                `${path}.value_type`,
                MESSAGES.RULES.ACTIONS.FILL.VALUE_TYPE.REQUIRED,
              );
            }
          });
        }
      }
    }
  });

  if (!fields.order) {
    errors.order = MESSAGES.RULE_GROUPS.ORDER;
  } else {
    const [rOrder, sOrder] = fields.order.toString().split('.');

    if (
      !rOrder ||
      parseInt(rOrder, 10) === 0 ||
      !_inRange(rOrder, 0, orderList.length + 2)
    ) {
      errors.order = MESSAGES.RULE_GROUPS.ORDER;
    }

    if (values.group) {
      if (
        sOrder &&
        (parseInt(sOrder, 10) === 0 ||
          !_inRange(sOrder, 0, values.group.rules.length + 2))
      ) {
        errors.order = MESSAGES.RULE_GROUPS.ORDER;
      }
    }
  }

  return errors;
};

// Calculate initial values for matching_pattern_element_from and matching_pattern_element_to fields
const getMatchingPatternFromToInitialValues = (action, getMatchingPattern) => {
  let matchingPatternFromTo = {};
  if (_get(action, 'mark_action.matching_pattern_element_from', false)) {
    const matching_pattern_element_from = getMatchingPattern(
      action.mark_action.matching_pattern_element_from,
    );
    matchingPatternFromTo = {
      ...matchingPatternFromTo,
      matching_pattern_element_from,
    };
  }

  if (_get(action, 'mark_action.matching_pattern_element_to', false)) {
    const matching_pattern_element_to = getMatchingPattern(
      action.mark_action.matching_pattern_element_to,
    );
    matchingPatternFromTo = {
      ...matchingPatternFromTo,
      matching_pattern_element_to,
    };
  }
  return matchingPatternFromTo;
};

export const createReduxFormInitialValues = (rule, entities) => {
  const matchingPatterns = _map(
    rule.matching_pattern_elements,
    (matchingPattern, index) => {
      const entity = _find(entities, {
        id: matchingPattern.entity,
      });

      if (entity) {
        return {
          ...matchingPattern,
          // Mix extra details about entity
          index,
          name: entity.name,
          color: entity.color,
          features: entity.features,
          archived: entity.archived,
        };
      }

      return {
        ...matchingPattern,
        name: '',
        color: '#444',
        features: [],
      };
    },
  );

  const getMatchingPattern = id => _find(matchingPatterns, { id });

  const conditions = _map(rule.conditions, cond => {
    if (cond.condition_type === CONDITION_TYPE.ENTITY) {
      return ih(cond, {
        entity_condition: {
          entity: {
            $apply: id => _find(entities, { id }),
          },
          matching_pattern_element: {
            $apply: getMatchingPattern,
          },
        },
      });
    }

    if (cond.condition_type === CONDITION_TYPE.FEATURE) {
      const matchingPattern = _find(matchingPatterns, {
        id: cond.entity_feature_condition.matching_pattern_element,
      });
      return ih(cond, {
        entity_feature_condition: {
          matching_pattern_element: {
            $set: matchingPattern,
          },
          entity_feature: {
            $apply: id => _find(matchingPattern.features, { id }),
          },
        },
      });
    }

    if (cond.condition_type === CONDITION_TYPE.REGEXP) {
      return ih(cond, {
        regexp_condition: {
          matching_pattern_element: {
            $apply: getMatchingPattern,
          },
        },
      });
    }

    return null;
  });

  const mix = item => {
    let ret = null;

    if (item) {
      const { source_entity_feature, source_matching_pattern_element } = item;

      if (source_matching_pattern_element) {
        const sourceMatchingPattern = _find(matchingPatterns, {
          id: source_matching_pattern_element,
        });

        ret = {
          source_entity_feature: null,
          source_matching_pattern_element: sourceMatchingPattern,
        };

        if (sourceMatchingPattern && source_entity_feature) {
          const sourceEntityFeature = _find(sourceMatchingPattern.features, {
            id: source_entity_feature,
          });

          ret = {
            ...ret,
            source_entity_feature: sourceEntityFeature,
          };
        }
      }
    }

    return ret;
  };

  const actions = _map(rule.actions, action => {
    if (
      action.action_type === ACTION_TYPE.MARK ||
      action.action_type === ACTION_TYPE.FILL
    ) {
      const entity =
        action.action_type === ACTION_TYPE.MARK
          ? _find(entities, { id: action.mark_action.entity })
          : _find(matchingPatterns, {
              id: action.fill_action.matching_pattern_element,
            });

      if (entity) {
        let key;
        let matchingPatternFromTo = {};

        if (action.action_type === ACTION_TYPE.MARK) {
          key = 'entity';

          matchingPatternFromTo = getMatchingPatternFromToInitialValues(
            action,
            getMatchingPattern,
          );
        } else {
          key = 'matching_pattern_element';
        }

        return ih(action, {
          [action.action_type]: {
            [key]: { $set: entity },
            $merge: matchingPatternFromTo,
            feature_values: {
              $apply: values =>
                entity.features.map(feature => {
                  const value = _find(values, { feature: feature.id });

                  return {
                    value_type: null,
                    explicit_value: null,

                    ...value,

                    dynamic_value: mix(value && value.dynamic_value),
                    concatenation_value:
                      value &&
                      value.concatenation_value &&
                      value.concatenation_value.map(item => mix(item)),

                    feature,
                  };
                }),
            },
          },
        });
      }
    }

    if (action.action_type === ACTION_TYPE.UNMARK) {
      return ih(action, {
        unmark_action: {
          matching_pattern_element: {
            $apply: getMatchingPattern,
          },
        },
      });
    }

    if (action.action_type === ACTION_TYPE.SHIFT) {
      return ih(action, {
        shift_action: {
          matching_pattern_element: {
            $apply: getMatchingPattern,
          },
          matching_pattern_element_from: {
            $apply: getMatchingPattern,
          },
          matching_pattern_element_to: {
            $apply: getMatchingPattern,
          },
        },
      });
    }

    return null;
  });

  return {
    // GENERAL TAB:
    name: rule.name,
    enabled: rule.enabled,
    description: rule.description,
    // MATCHING PATTERN TAB:
    entities: matchingPatterns,
    // CONDITIONS TAB:
    conditions,
    // ACTIONS TAB:
    actions,
  };
};

export const createRequestPayload = values => {
  const order =
    +values.order % 1 !== 0
      ? parseInt(values.order.toString().split('.')[1], 10) - 1
      : values.order - 1;

  return {
    order,
    name: values.name,
    enabled: values.enabled,
    description: values.description,
    rule_group: getId(values.group),
    context: getId(values.context),

    actions:
      values.actions.map(action => {
        if (
          action.action_type === ACTION_TYPE.MARK ||
          action.action_type === ACTION_TYPE.FILL
        ) {
          const key =
            action.action_type === ACTION_TYPE.MARK
              ? 'entity'
              : 'matching_pattern_element';

          const matchingPatternsToFrom =
            action.action_type === ACTION_TYPE.MARK
              ? {
                  matching_pattern_element_from: _get(
                    action,
                    'mark_action.matching_pattern_element_from.id',
                    null,
                  ),
                  matching_pattern_element_to: _get(
                    action,
                    'mark_action.matching_pattern_element_to.id',
                    null,
                  ),
                }
              : {};

          return ih(action, {
            [action.action_type]: {
              [key]: { $apply: getId },
              $merge: matchingPatternsToFrom,
              feature_values: {
                $apply: i =>
                  i
                    .filter(i => i.value_type)
                    .map(j => {
                      if (
                        j.value_type === FEATURE_VALUE_TYPE.EXPLICIT ||
                        j.value_type === FEATURE_VALUE_TYPE.COVER_TEXT
                      ) {
                        return ih(j, {
                          feature: { $apply: getId },
                        });
                      }

                      if (j.value_type === FEATURE_VALUE_TYPE.DYNAMIC) {
                        return ih(j, {
                          feature: { $apply: getId },
                          dynamic_value: {
                            source_entity_feature: { $apply: getId },
                            source_matching_pattern_element: { $apply: getId },
                          },
                        });
                      }

                      if (j.value_type === FEATURE_VALUE_TYPE.CONCATENATION) {
                        return ih(j, {
                          feature: { $apply: getId },
                          concatenation_value: {
                            $apply: x =>
                              x.map(y =>
                                ih(y, {
                                  source_entity_feature: { $apply: getId },
                                  source_matching_pattern_element: {
                                    $apply: getId,
                                  },
                                }),
                              ),
                          },
                        });
                      }
                      return null;
                    }),
              },
            },
          });
        }

        if (action.action_type === ACTION_TYPE.UNMARK) {
          return ih(action, {
            unmark_action: {
              matching_pattern_element: {
                $apply: getId,
              },
            },
          });
        }

        if (action.action_type === ACTION_TYPE.SHIFT) {
          return ih(action, {
            shift_action: {
              matching_pattern_element: {
                $apply: getId,
              },
              matching_pattern_element_from: {
                $apply: getId,
              },
              matching_pattern_element_to: {
                $apply: getId,
              },
            },
          });
        }

        return null;
      }) || [],

    conditions:
      values.conditions.map(cond => {
        if (cond.condition_type === CONDITION_TYPE.ENTITY) {
          return ih(cond, {
            entity_condition: {
              matching_pattern_element: { $apply: getId },
              entity: { $apply: getId },
            },
          });
        }

        if (cond.condition_type === CONDITION_TYPE.FEATURE) {
          return ih(cond, {
            entity_feature_condition: {
              matching_pattern_element: { $apply: getId },
              entity_feature: { $apply: getId },
            },
          });
        }

        if (cond.condition_type === CONDITION_TYPE.REGEXP) {
          return ih(cond, {
            regexp_condition: {
              matching_pattern_element: { $apply: getId },
            },
          });
        }

        return null;
      }) || [],

    matching_pattern_elements:
      values.entities.map((entity, index) => ({
        order: index,
        id: entity.id,
        entity: entity.entity,
        mandatory: entity.mandatory,
      })) || [],
  };
};
